import {
  ILiftsStat,
  IOpenStat,
  IResortPathInfo,
  IRunsStat,
  ISnowStatus,
  IWeatherItem,
  OpenFlag,
  SurfaceType,
} from 'api/resort/types';
import classNames from 'classnames';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { OpenStatus } from 'components/blocks/OpenStatus';
import { IconWInfo } from 'components/resort/IconWInfo';
import { regionShowKmFallbackToAmountInSummary } from 'i18n/country';
import { useDistanceFormatter } from 'i18n/unitFormat';
import { useOTSTranslation, useSurfaceTypeTranslation } from 'i18n/useOTSTranslation';
import { ReactComponent as SvgLifts } from 'public/assets/icons/lifts.svg';
import { ReactComponent as SvgRuns } from 'public/assets/icons/runs.svg';
import { ReactComponent as SvgBase } from 'public/assets/icons/snow-depth-base.svg';
import { ReactComponent as SvgSummit } from 'public/assets/icons/snow-depth-summit.svg';
import React, {
  useCallback, useEffect, useState,
} from 'react';
import { PopupFeedback } from 'components/resort/Summary/PopupFeedback';
import { formatDateFromStr, SHORT_DATE_FORMAT } from 'util/formatDate';
import { SvgLinkTr } from 'components/icons';
import { isEmpty } from 'util/isEmpty';
import { BLUR_CONTENT_STYLE_NAME } from 'theme';
import TodaysWeather from 'components/resort/Summary/TodaysWeather';
import RecentSnowFall from 'components/resort/Summary/RecentSnowfall';
import ProjectedDateBlock from 'components/resort/Summary/ProjectedDateBlock';

import styles from './styles.module.scss';

interface ISummaryProps {
  resortCountry?: string,
  surface: SurfaceType,
  surfaceBase: SurfaceType,
  isFresh: boolean,
  resort: IResortPathInfo,
  weather?: IWeatherItem,
  updated: string,
  snow: ISnowStatus,
  lifts?: ILiftsStat,
  runs?: IRunsStat,
  status: IOpenStat,
  runsKm?: IRunsStat,
  isMetricUnits: boolean
}

export const Summary = (
  {
    isFresh,
    weather,
    updated,
    snow,
    lifts,
    runs,
    surface,
    surfaceBase,
    status,
    resortCountry,
    resort,
    runsKm,
    isMetricUnits,
  }: ISummaryProps,
) => {
  const eS = useSurfaceTypeTranslation();
  const { t } = useOTSTranslation('common');
  const inchFormat = useDistanceFormatter('cmOrInchShort');

  const formatFn = useDistanceFormatter('kmOrMile');
  const showClosing = status.openFlag && [OpenFlag.Open, OpenFlag.WeekendsOnly, OpenFlag.NoReportAvailable].includes(status.openFlag);
  const showOpening = status.openFlag && [OpenFlag.Closed].includes(status.openFlag);
  const isAvailable = status.openFlag && ![OpenFlag.NoReportAvailable].includes(status.openFlag);
  const showRunLifts = status.openFlag && status.openFlag !== OpenFlag.Closed && status.openFlag !== OpenFlag.NoReportAvailable;
  const [isOpenPopup, toggleIsOpenPopup] = useState<boolean>(false);
  const openedRunsKm = runsKm?.open || undefined;
  const totalRunsKm = (openedRunsKm && runsKm?.total) || undefined;

  const togglePopup = useCallback(() => {
    toggleIsOpenPopup(!isOpenPopup);
  }, [isOpenPopup]);

  const preferKm = regionShowKmFallbackToAmountInSummary(resortCountry);

  useEffect(() => {
    if (isOpenPopup) {
      document.body.classList.add('overflowHidden');
    } else {
      document.body.classList.remove('overflowHidden');
    }

    return () => {
      document.body.classList.remove('overflowHidden');
    };
  }, [isOpenPopup]);

  return (
    <BoxWithShadow key={`${isFresh ? '1' : '0'}`}>
      {isOpenPopup && (
        <PopupFeedback
          togglePopup={togglePopup}
          resort={resort.title_original}
          id={resort.uuid}
        />
      )}
      <div className={classNames(styles.wrapper, isFresh ? '' : BLUR_CONTENT_STYLE_NAME)}>
        <div className={styles.header}>
          <span className={classNames('h2', styles.h2)}>
            {resort.title}
            :&nbsp;
            <OpenStatus flag={status.openFlag} />
          </span>
          <time className={styles.updatedAt}>
            {t('resort.skiReport.lastUpdated')}
            &nbsp;
            {formatDateFromStr(updated, SHORT_DATE_FORMAT)}
          </time>
        </div>
        <div className={classNames(styles.box)}>
          <div className={styles.info}>
            <div className={styles.snow}>
              {snow.base !== null && (
                <div>
                  <IconWInfo
                    text={inchFormat(snow.base || 0, undefined, 'common.na')}
                    top={t('common.base')}
                    bottom={eS(surfaceBase, 'resort.skiReport.surfaceNotAvailable')}
                    Icon={SvgBase}
                    title={t('common.base')}
                  />
                </div>
              )}
              {snow.summit !== null && (
                <div>
                  <IconWInfo
                    text={inchFormat(snow.summit || 0, undefined, 'common.na')}
                    top={t('common.summit')}
                    bottom={eS(surface, 'resort.skiReport.surfaceNotAvailable')}
                    Icon={SvgSummit}
                    title={t('common.summit')}
                  />
                </div>
              )}
              {showRunLifts && lifts && (
                <div>
                  <IconWInfo
                    text={`${lifts.open ?? t('common.dash')} / ${lifts.total ?? t('common.dash')}`}
                    bottom={t('resort.skiReport.liftOpen')}
                    Icon={SvgLifts}
                    title={t('resort.skiReport.liftOpen')}
                  />
                </div>
              )}
              {showRunLifts && runs && !preferKm && (
                <div>
                  <IconWInfo
                    text={`${runs.open ?? t('common.dash')} / ${runs.total ?? t('common.dash')}`}
                    bottom={t('resort.skiReport.runsOpen')}
                    Icon={SvgRuns}
                    title={t('resort.skiReport.runsOpen')}
                  />
                </div>
              )}
              {showRunLifts && runs && preferKm && (
                <div>
                  <IconWInfo
                    text={
                      runsKm
                        ? (formatFn([openedRunsKm, totalRunsKm], '/', 'common.na'))
                        : (`${runs.open ?? t('common.dash')}/${runs.total ?? t('common.dash')}`)
                    }
                    bottom={t('resort.skiReport.kmOpen')}
                    Icon={SvgRuns}
                    title={t('resort.skiReport.runsOpen')}
                    className={classNames({ [styles.metric]: isMetricUnits })}
                  />
                </div>
              )}
            </div>
            {status.openFlag !== OpenFlag.NoReportAvailable && showClosing && status?.closingDate && (
              <ProjectedDateBlock date={status.closingDate} title="resort.skiReport.projectedClosing" />
            )}
            {showOpening && status?.openingDate && (
              <ProjectedDateBlock date={status.openingDate} title="resort.skiReport.projectedOpening" isOpening />
            )}
            <button
              type="button"
              className={styles.fhBtnPopup}
              onClick={togglePopup}
            >
              {t('resort.skiReport.provideFeedback')}
              <SvgLinkTr />
            </button>
          </div>
          <div className={styles.status}>
            {isAvailable && status.openFlag !== OpenFlag.NoReportAvailable && (!!snow.last24 || !!snow.last72) && (
              <RecentSnowFall snow={snow} />
            )}
            {(!isEmpty(weather?.base?.max) || !isEmpty(weather?.base?.min) || !!weather?.type) && (
              <TodaysWeather resort={resort} type={weather?.type} base={weather?.base} />
            )}
          </div>
        </div>
      </div>
    </BoxWithShadow>
  );
};
